import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCreateZakazka } from "@/api/hooks/useZakazky";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  ZakazkaPolia,
  type ZakazkaPoliaHodnoty,
} from "@/components/zakazka/ZakazkaPolia";

const PRAZDNE: ZakazkaPoliaHodnoty = {
  cislo: "",
  nazov: "",
  bezHran: true,
  druh: "vypocet",
};

export interface ZakazkaCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Otvorená kategória prehľadu — nová zákazka sa do nej zaradí (KOLO 172). */
  kategoriaId?: string | null;
}

/**
 * Nová zákazka — rovnaké polia ako Upraviť zákazku (KOLO 162). Pri
 * vytvorení sa vyberá druh (KOLO 200): výpočet (V… pridelí server), zákazka
 * AA (zadané číslo) alebo malá zákazka bez AA (Z… pridelí server).
 */
export function ZakazkaCreateDialog({
  open,
  onOpenChange,
  kategoriaId = null,
}: ZakazkaCreateDialogProps): JSX.Element {
  const navigate = useNavigate();
  const createMutation = useCreateZakazka();
  const [polia, setPolia] = useState<ZakazkaPoliaHodnoty>(PRAZDNE);

  useEffect(() => {
    if (!open) setPolia(PRAZDNE);
  }, [open]);

  const druh = polia.druh ?? "vypocet";
  const chybaCisla = druh === "zakazka" && !polia.cislo.trim();

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (chybaCisla) return;
    try {
      const response = await createMutation.mutateAsync({
        cislo_zakazky: druh === "zakazka" ? polia.cislo.trim() : null,
        bez_aa: druh === "mala",
        nazov: polia.nazov.trim() || null,
        rozmery_bez_hran: polia.bezHran,
        montaz: true,
        doprava: true,
        ...(kategoriaId ? { kategoria_id: kategoriaId } : {}),
      });
      toast({
        title: "Zákazka vytvorená",
        description: `Zákazka ${response.data.cislo_zakazky} je pripravená na editáciu.`,
      });
      onOpenChange(false);
      navigate(`/zakazky/${response.data.id}`);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri vytváraní zákazky",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nová zákazka</DialogTitle>
          <DialogDescription>
            Výpočet a malá zákazka dostanú číslo automaticky. Zostavy a dielce
            pridáš v editore.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
          <ZakazkaPolia
            hodnoty={polia}
            onChange={setPolia}
            idPrefix="zn"
            cisloPovinne={false}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Zrušiť
            </Button>
            <Button type="submit" disabled={createMutation.isPending || chybaCisla}>
              {createMutation.isPending ? "Vytvára sa…" : "Vytvoriť"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
